import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, FormArray, FormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDatepickerMaskDirective } from '@anihis/shared/src/shared/directive/mat-datepicker-mask.directive';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { getMinAnimalBirthDate, getMaxBirthDate } from '@anihis/shared/src/validators/birth-date.validators';
import { inject } from '@angular/core';

@Component({
  selector: 'anihis-owner-statement-debt-installments',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatDatepickerModule,
    MatDatepickerMaskDirective,
    MatNativeDateModule,
    MatIconModule,
    MatButtonModule,
    TranslocoModule,
  ],
  templateUrl: './owner-statement-debt-installments.component.html',
  styleUrls: ['./owner-statement-debt-installments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OwnerStatementDebtInstallmentsComponent
  extends FormBaseComponent
  implements OnInit
{
  minDate = getMinAnimalBirthDate();
  today = getMaxBirthDate();

  @Output() formChange = new EventEmitter<any>();

  override ngOnInit(): void {
    this.form = this.fb.group(
      {
        totalAmount: [''],
        periodFrom: [''],
        periodTo: [''],
        numberOfInstallments: [1],
        installments: this.fb.array([this.createInstallmentGroup(1)]),
        createdDate: [''],
        totalDebt: [''],
        note: [''],
        veterinarianName: [''],
      },
      {
        validators: [
          this.periodRangeValidator,
          this.totalAmountExceededValidator,
        ],
      },
    );

    this.formChange.emit(this.form.value);
    this.subs.sink = this.form.valueChanges.subscribe((v) =>
      this.formChange.emit(v),
    );

    // Keep totalDebt synchronized with the sum of installment amounts
    this.subs.sink = this.installments.valueChanges.subscribe(() => {
      this.updateTotalDebt();
      this.cd.markForCheck();
    });
    // initialize totalDebt based on initial installment
    this.updateTotalDebt();

    // Watch numberOfInstallments: auto-trim excess rows
    this.subs.sink = this.form
      .get('numberOfInstallments')!
      .valueChanges.subscribe((raw) => {
        const max = parseInt(String(raw ?? ''), 10);
        if (!isNaN(max) && max > 0) {
          while (this.installments.length > max) {
            this.installments.removeAt(this.installments.length - 1);
          }
          this.updateTotalDebt();
        }
        this.cd.markForCheck();
      });

    // Watch totalAmount: re-suggest remaining on last row when total changes
    this.subs.sink = this.form
      .get('totalAmount')!
      .valueChanges.subscribe(() => {
        this.cd.markForCheck();
      });

    super.ngOnInit();
  }

  createInstallmentGroup(index: number) {
    return this.fb.group(
      {
        installmentNumber: [index],
        amount: [''],
        date: [''],
      },
      { validators: this.installmentValidator },
    );
  }

  // Ensure that if an amount is entered for an installment, a date must be provided
  installmentValidator = (group: FormGroup) => {
    const amount = group.get('amount')?.value;
    const date = group.get('date')?.value;
    const hasAmount =
      amount !== null && amount !== undefined && String(amount).trim() !== '';
    if (hasAmount && !date) {
      return { dateRequiredForAmount: true };
    }
    return null;
  };

  // Validator to ensure sum of installment amounts equals totalAmount
  totalAmountExceededValidator = (group: FormGroup) => {
    const rawTotal = group.get('totalAmount')?.value;
    if (
      rawTotal === null ||
      rawTotal === undefined ||
      String(rawTotal).trim() === ''
    )
      return null;
    const total = parseFloat(String(rawTotal).replace(/,/g, '.'));
    if (isNaN(total)) return null;
    let sum = 0;
    const installments = group.get('installments') as FormArray;
    for (const c of installments.controls) {
      const val = c.get('amount')?.value;
      if (val === null || val === undefined || val === '') continue;
      const num =
        typeof val === 'number'
          ? val
          : parseFloat(String(val).replace(/,/g, '.'));
      if (!isNaN(num)) sum += num;
    }
    return Math.round(sum * 100) !== Math.round(total * 100)
      ? { installmentSumMismatch: true }
      : null;
  };

  // Validator to ensure periodFrom is not after periodTo
  periodRangeValidator = (group: FormGroup) => {
    const from = group.get('periodFrom')?.value;
    const to = group.get('periodTo')?.value;
    if (!from || !to) return null;
    const f = new Date(from);
    const t = new Date(to);
    if (isNaN(f.getTime()) || isNaN(t.getTime())) {
      return null;
    }
    return f <= t ? null : { periodRangeInvalid: true };
  };

  get installments(): FormArray {
    return this.form.get('installments') as FormArray;
  }

  /** Total amount entered by the user, or NaN if empty/invalid. */
  private get parsedTotal(): number {
    const raw = this.form?.get('totalAmount')?.value;
    if (raw === null || raw === undefined || String(raw).trim() === '')
      return NaN;
    return parseFloat(String(raw).replace(/,/g, '.'));
  }

  /** Sum of all entered installment amounts. */
  private get sumEntered(): number {
    let sum = 0;
    for (const c of this.installments.controls) {
      const val = c.get('amount')?.value;
      if (val === null || val === undefined || val === '') continue;
      const num =
        typeof val === 'number'
          ? val
          : parseFloat(String(val).replace(/,/g, '.'));
      if (!isNaN(num)) sum += num;
    }
    return sum;
  }

  /** Remaining debt = totalAmount - sum. Returns null when not calculable. */
  get remainingAmount(): number | null {
    const total = this.parsedTotal;
    if (isNaN(total)) return null;
    const remaining = Math.round((total - this.sumEntered) * 100) / 100;
    return remaining > 0 ? remaining : null;
  }

  get canAddInstallment(): boolean {
    const max = this.form?.get('numberOfInstallments')?.value;
    if (max === null || max === undefined || String(max).trim() === '')
      return true;
    const maxNum = parseInt(String(max), 10);
    if (isNaN(maxNum) || maxNum <= 0) return true;
    return this.installments.length < maxNum;
  }

  addInstallment() {
    if (!this.canAddInstallment) return;
    const idx = this.installments.length + 1;
    const group = this.createInstallmentGroup(idx);
    // Pre-fill the new (last) row with the remaining debt if calculable
    const remaining = this.remainingAmount;
    if (remaining !== null) {
      group.get('amount')?.setValue(remaining, { emitEvent: false });
    }
    this.installments.push(group);
    this.form
      .get('numberOfInstallments')
      ?.setValue(this.installments.length, { emitEvent: false });
    this.updateTotalDebt();
    this.cd.markForCheck();
  }

  removeInstallment(i: number) {
    this.installments.removeAt(i);
    // renumber
    this.installments.controls.forEach((c, idx) =>
      c.get('installmentNumber')?.setValue(idx + 1, { emitEvent: false }),
    );
    this.form
      .get('numberOfInstallments')
      ?.setValue(this.installments.length, { emitEvent: false });
    this.updateTotalDebt();
  }

  updateTotalDebt() {
    let sum = 0;
    for (const c of this.installments.controls) {
      const val = c.get('amount')?.value;
      if (val === null || val === undefined || val === '') continue;
      // accept numbers or number-like strings, remove commas
      const num =
        typeof val === 'number'
          ? val
          : parseFloat(String(val).replace(/,/g, '.'));
      if (!isNaN(num)) sum += num;
    }
    // set formatted value with two decimals
    const formatted = Math.round(sum * 100) / 100;
    this.form.get('totalDebt')?.setValue(formatted, { emitEvent: false });
  }
}
